/**
 * Registry of surfaces that crest extraction can run against.
 *
 * Mirrors `alignmentDrapeSourcesAtom`: producers register under an owner key
 * (their layer id) and registration REPLACES that owner's whole set. Samplers
 * read raw file coordinates, so a producer must only register a layer whose
 * transform is the identity.
 */
import type { DrapeSampler } from '@/core/alignment';
import type { CrestResult } from '@/lib/crestLine/types';
import { atom } from 'jotai';

export interface CrestSource {
  layerId: string;
  layerName: string;
  sampler: DrapeSampler;
  /**
   * Every `<Surface>` this layer's `sampler` resolves over, in DOCUMENT
   * ORDER — which is also the order `combineSamplers` tries them in: the
   * first surface that covers a point wins. A Civil 3D design export
   * routinely ships "Existing Ground" alongside the design surface, so with
   * more than one entry here the extraction is genuinely ambiguous. The
   * dialog discloses this rather than resolving it silently (spec §9 wanted a
   * full SurfacePicker; disclosure is the honest interim).
   */
  surfaceNames: readonly string[];
}

const registryAtom = atom<{ byId: Map<string, CrestSource>; byOwner: Map<string, string[]> }>({
  byId: new Map(),
  byOwner: new Map(),
});

export const crestSourcesAtom = atom((get) => get(registryAtom).byId);

export const registerCrestSourcesAtom = atom(
  null,
  (get, set, { ownerKey, sources }: { ownerKey: string; sources: CrestSource[] }) => {
    const prev = get(registryAtom);
    const prevIds = prev.byOwner.get(ownerKey) ?? [];
    // Bail out without writing if this owner is republishing an identical
    // set. Consumers (e.g. the crest-extraction dialog, task 7) memoise
    // expensive extraction work on the CrestSource object they pulled out of
    // crestSourcesAtom, and the LandXML publishing effect re-runs on
    // unrelated layerSettings changes (e.g. an opacity tweak) — without this
    // check every such tweak would allocate a new Map, invalidate every
    // consumer's memo, and re-run a full extraction. crestSourcesFromEntries
    // always allocates fresh CrestSource objects, so identity must be judged
    // by field (layer id order, name, and sampler reference) rather than by
    // object reference. Do not remove this as a micro-optimisation.
    if (prevIds.length === sources.length) {
      let same = true;
      for (let i = 0; i < prevIds.length; i++) {
        const prevSource = prev.byId.get(prevIds[i]);
        const nextSource = sources[i];
        if (
          !prevSource ||
          prevSource.layerId !== nextSource.layerId ||
          prevSource.layerName !== nextSource.layerName ||
          prevSource.sampler !== nextSource.sampler ||
          prevSource.surfaceNames.length !== nextSource.surfaceNames.length ||
          prevSource.surfaceNames.some((n, j) => n !== nextSource.surfaceNames[j])
        ) {
          same = false;
          break;
        }
      }
      if (same) return;
    }

    const byId = new Map(prev.byId);
    const byOwner = new Map(prev.byOwner);
    for (const id of prevIds) byId.delete(id);
    for (const s of sources) byId.set(s.layerId, s);
    byOwner.set(
      ownerKey,
      sources.map((s) => s.layerId),
    );
    set(registryAtom, { byId, byOwner });
  },
);

export const unregisterCrestSourcesAtom = atom(null, (_get, set, ownerKey: string) => {
  set(registerCrestSourcesAtom, { ownerKey, sources: [] });
});

/**
 * The crest currently being previewed, or null when no extraction is open.
 * Lives here rather than in the dialog so the 3D overlay does not have to
 * import a component to read state.
 */
export const crestPreviewAtom = atom<CrestResult | null>(null);

export interface CrestExportRequest {
  layerId: string;
  layerName: string;
  featureIds: string[];
}

/**
 * Set by `CrestLineDialog`'s save-success toast action; read by `Layers.tsx`,
 * which opens the vector export (`useLaunchAnalysis`).
 *
 * `CrestLineDialog` closes itself as soon as a save fully succeeds (so it can
 * no longer host an in-dialog "Export as DXF" button/dialog the way an
 * earlier revision did), so the request has to outlive the dialog. It lives
 * here rather than as `Layers.tsx` component state so `CrestLineDialog` does
 * not need a callback prop threaded down from wherever it happens to be
 * opened.
 */
export const crestExportRequestAtom = atom<CrestExportRequest | null>(null);

/** True while the draw-guide picking tool is active. */
export const crestGuideModeAtom = atom(false);

/** Vertices clicked so far by the draw-guide tool, as XY pairs. */
export const crestGuideDraftAtom = atom<Array<[number, number]>>([]);

/**
 * The same sketch WITH elevations, for rendering only.
 *
 * `crestGuideDraftAtom` is deliberately XY (guideFromXY drops Z — only the
 * station axis and section normal matter), but the 3D overlay has to draw the
 * sketch at the picked surface height rather than at z=0. Every mutation
 * below touches both mirrors together; nothing may write one on its own.
 *
 * It lives here, rather than as component state inside `CrestGuideTool`,
 * because the draw toolbar (`CrestGuideToolbar`) is a sibling of the `<Canvas>`
 * — `FloatingToolbar` renders plain DOM — and its Undo / Cancel buttons must
 * go through exactly the same mutations as the tool's Backspace / Escape.
 */
export const crestGuideDraft3DAtom = atom<Array<[number, number, number]>>([]);

/**
 * Fewest vertices a sketch needs before it can become a guide.
 *
 * `guideStations` returns an empty station list for `n < 2` (and
 * `CrestLineDialog`'s commit effect refuses a shorter draft), so a one-point
 * sketch can never produce a crest. `crestGuideFinishAtom` below refuses to
 * commit under this count and the toolbar's Finish button is disabled under
 * it — the PRECONDITION is shared, not just the mutation, so the keyboard
 * path cannot do what the disabled button will not.
 */
export const CREST_GUIDE_MIN_VERTICES = 2;

/** Append one picked vertex (both mirrors, always together). */
export const crestGuideAddVertexAtom = atom(null, (_get, set, p: [number, number, number]) => {
  set(crestGuideDraftAtom, (d) => [...d, [p[0], p[1]] as [number, number]]);
  set(crestGuideDraft3DAtom, (d) => [...d, p]);
});

/** Drop the last picked vertex — Backspace / Ctrl+Z and the toolbar's Undo. */
export const crestGuideUndoVertexAtom = atom(null, (_get, set) => {
  set(crestGuideDraftAtom, (d) => d.slice(0, -1));
  set(crestGuideDraft3DAtom, (d) => d.slice(0, -1));
});

/**
 * Finish: leave draw mode with the sketch INTACT. That mode:false +
 * non-empty-draft transition is the commit signal `CrestLineDialog` watches
 * for. Enter and the toolbar's Finish button are the same call.
 *
 * Below `CREST_GUIDE_MIN_VERTICES` this is a NO-OP — draw mode stays on and
 * the sketch is kept. The precondition lives here, in the shared write,
 * rather than only in the toolbar's `disabled` prop: `CrestGuideTool`'s Enter
 * handler calls the same atom, and with the check in the button alone Enter
 * on a 1-vertex sketch left draw mode anyway, the dialog's commit effect
 * ignored the too-short draft, and the sketch was silently thrown away with
 * no feedback. A disabled button and a no-op key are now the same rule.
 */
export const crestGuideFinishAtom = atom(null, (get, set) => {
  if (get(crestGuideDraftAtom).length < CREST_GUIDE_MIN_VERTICES) return;
  set(crestGuideModeAtom, false);
});

/**
 * Cancel: discard the sketch, then leave draw mode. Clearing first is what
 * keeps the dialog's commit effect from seeing a committed guide. Escape and
 * the toolbar's Cancel button are the same call.
 */
export const crestGuideCancelAtom = atom(null, (_get, set) => {
  set(crestGuideDraftAtom, []);
  set(crestGuideDraft3DAtom, []);
  set(crestGuideModeAtom, false);
});

/** Clear the sketch without leaving draw mode (used when a session starts). */
export const crestGuideClearDraftAtom = atom(null, (_get, set) => {
  set(crestGuideDraftAtom, []);
  set(crestGuideDraft3DAtom, []);
});

/**
 * True while a canvas click belongs to a crest-line picking tool and must
 * therefore do NOTHING ELSE.
 *
 * `CrestGuideTool` listens for DOM `click` on `renderer.domElement` in the
 * bubble phase (like `AddLineTool`/`MeasuringTool`, because Potree octrees
 * are invisible to R3F's raycaster). The app's own feature picking is R3F
 * `onClick` on the same canvas, so without a guard the SAME click both
 * places a guide vertex and selects the feature under it — every vertex the
 * user places pops the LandXML properties drawer open.
 *
 * Every pick handler that can open a drawer or change the selection reads
 * THIS atom rather than `crestGuideModeAtom` directly, so "picking is
 * suppressed by the crest tool" has exactly one definition to keep in step:
 *
 *   - `LandXML.tsx`                `_handleDoubleClick` (LandXML surfaces/alignments drawer,
 *                                                       via `toolPickSuppressedAtom`, which
 *                                                       composes this atom with the drawing tools)
 *   - `GeoJson.tsx`                `handleClick`       (vector feature selection)
 *   - `3D/SavedPipes.tsx`          `handleSelect`       (pipe feature selection)
 *   - `3D/SceneContextMenuPicker.tsx` `toolActive`      (suppresses dblclick-to-center)
 *   - `IFC2.tsx`                   `handleDoubleClick`  (IFC fragment element pick,
 *                                                       via `toolPickSuppressedAtom`)
 *
 * Scoped to draw mode alone: merely having `CrestLineDialog` open leaves
 * `crestGuideModeAtom` false, and normal picking is untouched.
 */
export const crestPickSuppressedAtom = atom((get) => get(crestGuideModeAtom));

/**
 * Which loaded LandXML entries may be extracted from.
 *
 * Split out as a pure function so the identity-transform guard is testable —
 * a sampler reads RAW FILE coordinates and is silently wrong once the layer
 * has been moved or rotated, which is the kind of bug that produces a
 * plausible-looking crest in the wrong place.
 */
export function crestSourcesFromEntries(
  entries: ReadonlyArray<{
    layerId: string;
    layerName: string;
    sampler: DrapeSampler | null;
    surfaceNames?: readonly string[];
  }>,
  isIdentity: (layerId: string) => boolean,
): CrestSource[] {
  const out: CrestSource[] = [];
  for (const e of entries) {
    if (!e.sampler || !isIdentity(e.layerId)) continue;
    out.push({
      layerId: e.layerId,
      layerName: e.layerName,
      sampler: e.sampler,
      surfaceNames: e.surfaceNames ?? [],
    });
  }
  return out;
}
